import { ajax } from 'rxjs/ajax';
import { catchError, map } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { API_HOST } from '@support/api';
import localForage from 'localforage';
import { appDesktopProtocol } from '@app/config';

export type AuthPlatform = 'apple' | 'google';

/**
 * The access token expires every 5 minutes. To avoid race conditions,
 * we're expiring it 1 minute early.
 */
const EXPIRATION_BUFFER_MS = 60 * 1000;

export function isAccessTokenValid(): boolean {
  const jwt = checkStorageForAccessToken();
  if (!jwt) {
    return false;
  }

  try {
    const payload = parseJwt(jwt);
    const expiresMS = payload.exp * 1000;
    return !(expiresMS < Date.now() + EXPIRATION_BUFFER_MS);
  } catch (_) {
    return false;
  }
}

export function isRefreshTokenValid(): boolean {
  const refresh = checkStorageForRefreshToken();
  if (!refresh) {
    return false;
  }

  try {
    const payload = parseJwt(refresh);
    const expiresMS = payload.exp * 1000;
    return !(expiresMS < Date.now() + EXPIRATION_BUFFER_MS);
  } catch (_) {
    return false;
  }
}

export function checkStorageForAccessToken(): string | null {
  return localStorage.getItem('access');
}

export function checkStorageForRefreshToken(): string | null {
  return localStorage.getItem('refresh');
}

export function storeJWTToken(access: string, refresh: string): void {
  localStorage.setItem('access', access);
  localStorage.setItem('refresh', refresh);
  handoffTokenToDesktop();
}

function storeSignInWithApplePII(credential: AppleAuthCredential): void {
  localStorage.setItem(
    'siwa_pii',
    JSON.stringify({
      firstName: credential.firstName,
      lastName: credential.lastName,
      email: credential.email,
    }),
  );
}

export function checkStorageForSignInWithApplePII(): AppleAuthCredential | null {
  const value = localStorage.getItem('siwa_pii');
  if (!value) {
    return null;
  }
  return JSON.parse(value);
}

export function clearSignInWithApplePII(): void {
  localStorage.removeItem('siwa_pii');
}

export function handoffTokenToDesktop(): void {
  if (!window.app.isElectron || !window.desktopIntegrations || !isAccessTokenValid()) {
    return;
  }
}

export function clearJWTToken(): void {
  localStorage.removeItem('access');
  localStorage.removeItem('refresh');
}

function storeTemporaryAccess(token: string, provider: AuthPlatform): void {
  sessionStorage.setItem(
    'auth_request_token',
    JSON.stringify({
      token,
      provider,
    }),
  );
}

export function clearTemporaryAccess(): void {
  sessionStorage.removeItem('auth_request_token');
}

interface TemporaryAccess {
  token: string;
  provider: AuthPlatform;
}

export function checkStorageForTemporaryAccess(): TemporaryAccess | null {
  const value = sessionStorage.getItem('auth_request_token');
  if (!value) {
    return null;
  }
  return JSON.parse(value);
}

interface JWTPayload {
  token_type: 'access' | 'refresh';
  exp: number;
  iat: number;
  jti: string;
  user_id: number;
}

export function parseJwt(token: string): JWTPayload {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
}

export interface JWTResponse {
  access: string;
  refresh: string;
}

export class UnauthorizedError extends Error {
  constructor() {
    super('User needs to authenticate.');
  }
}

export function getOrRefreshAccessToken(): Observable<string> {
  if (isAccessTokenValid()) {
    return of(checkStorageForAccessToken());
  }
  if (!isRefreshTokenValid()) {
    clearJWTToken();
    return throwError(() => new UnauthorizedError());
  }

  return refreshAuth(checkStorageForRefreshToken()).pipe(
    map(({ access }) => access),
    catchError((_) => {
      return throwError(() => new UnauthorizedError());
    }),
  );
}

export function refreshAuth(refreshToken: string): Observable<JWTResponse> {
  return ajax
    .post<JWTResponse>(`${API_HOST}api/token/refresh/`, {
      refresh: refreshToken,
    })
    .pipe(
      map(({ response }) => {
        const { access, refresh } = response;
        storeJWTToken(access, refresh);
        return response;
      }),
    );
}

const NEW_SIGN_UP_STORAGE_KEY = 'new_sign_up';

export function setNewMarketingSignUp(provider: AuthPlatform | 'email'): void {
  localStorage.setItem(NEW_SIGN_UP_STORAGE_KEY, provider);
}

export function isNewMarketingSignUp(): boolean {
  return !!localStorage.getItem(NEW_SIGN_UP_STORAGE_KEY);
}

export function clearNewSignUp(): void {
  localStorage.removeItem(NEW_SIGN_UP_STORAGE_KEY);
}

interface AuthWorkflowResponse {
  token: string;
  redirect: string;
}

export function beginAuthWorkflow(
  provider: AuthPlatform,
  isNewSignUp?: boolean,
  referralCode?: string,
): Observable<AuthWorkflowResponse> {
  clearTemporaryAccess();
  if (isNewSignUp) {
    setNewMarketingSignUp(provider);
  }
  storeAuthPlatform(provider);
  const params = [`platform=${provider}`];
  if (window.app.isElectron) {
    params.push(`device=desktop`, `redirect_url=${encodeURIComponent(appDesktopProtocol)}`);
  } else {
    params.push(`redirect_url=${encodeURIComponent(location.origin)}`);
  }
  if (isNewSignUp && referralCode) {
    params.push(`referral=${encodeURIComponent(referralCode)}`);
  }

  return ajax
    .get<AuthWorkflowResponse>(
      `${API_HOST}api/v1/users/auth/${isNewSignUp ? 'register' : 'login'}/?${params.join('&')}`,
    )
    .pipe(
      map(({ response }) => {
        const { token } = response;
        storeTemporaryAccess(token, provider);
        return response;
      }),
      catchError((err) => {
        return throwError(() => err);
      }),
    );
}

export function completeEmailAuthWorkflow(
  token: string,
  email: string,
  isSignUp: boolean,
): Observable<JWTResponse> {
  const endpoint = isSignUp ? 'complete-email-signup' : 'complete-email-login';
  return ajax.post<JWTResponse>(`${API_HOST}api/v2/auth/${endpoint}/`, { email, token }).pipe(
    map(({ response }) => {
      const { access, refresh } = response;
      storeJWTToken(access, refresh);
      storeAuthPlatform('email');
      return response;
    }),
    catchError((err) => {
      console.error(err);
      return throwError(() => err);
    }),
  );
}

export interface AppleAuthCredential {
  code: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export function completeSignInWithAppleAuthWorkflow(
  credential: AppleAuthCredential,
  isRegister: boolean,
): Observable<JWTResponse> {
  return ajax
    .post<JWTResponse>(
      `${API_HOST}api/v1/rest-auth/apple/${isRegister ? 'register' : 'login'}/?type=jwt`,
      {
        code: credential.code,
        client_id: 'com.clay.ios',
        user: {
          name: {
            firstName: credential.firstName || '',
            lastName: credential.lastName || '',
          },
          email: credential.email || '',
        },
      },
    )
    .pipe(
      map(({ response }) => {
        const { access, refresh } = response;
        storeJWTToken(access, refresh);
        storeSignInWithApplePII(credential);
        clearTemporaryAccess();
        return response;
      }),
      catchError((err) => {
        // something failed with the auth token we had, so get rid of it
        clearTemporaryAccess();
        return throwError(() => err);
      }),
    );
}

export function completeAuthWorkflow(
  token: string,
  provider: AuthPlatform,
): Observable<JWTResponse> {
  return ajax
    .post<JWTResponse>(`${API_HOST}api/v1/users/auth/login/?platform=${provider}`, {
      token,
    })
    .pipe(
      map(({ response }) => {
        const { access, refresh } = response;
        storeJWTToken(access, refresh);
        clearTemporaryAccess();
        return response;
      }),
      catchError((err) => {
        // something failed with the auth token we had, so get rid of it
        clearTemporaryAccess();
        return throwError(() => err);
      }),
    );
}

export function getAuthorizationError(): { error: string; provider?: AuthPlatform } | null {
  const value = sessionStorage.getItem('auth_error');
  if (!value) {
    return null;
  }
  return JSON.parse(value);
}

export function clearAuthorizationError(): void {
  sessionStorage.removeItem('auth_error');
}

export function handleAuthorizationError(error: string, provider?: AuthPlatform): void {
  clearTemporaryAccess();
  sessionStorage.setItem(
    'auth_error',
    JSON.stringify({
      error,
      provider,
    }),
  );
}

export function cleanupAndLogout(): void {
  localForage.clear();
  clearTemporaryAccess();
  clearJWTToken();
  if (window.app.isElectron && window.desktopIntegrations) {
    window.desktopIntegrations.logout();
  }
  window.location.href = '/login';
}

export function storeAuthPlatform(platform: AuthPlatform | 'email'): void {
  localStorage.setItem('previous_auth_platform', platform);
}

export function previousAuthPlatform(): AuthPlatform | 'email' | null {
  const value = localStorage.getItem('previous_auth_platform');
  if (!value || !['apple', 'google', 'email'].includes(value)) {
    return null;
  }
  return value as AuthPlatform | 'email';
}

export function storeLastAuthEmail(email: string): void {
  localStorage.setItem('previous_auth_email', email);
}

export function previousAuthEmail(): string | null {
  return localStorage.getItem('previous_auth_email');
}
